import { useState, useEffect, useCallback } from 'react';

/**
 * Web3 Wallet Connect Hook for Governance
 *
 * Handles MetaMask/injected wallet authentication
 * Auto-registers users via wallet signature
 * Manages WordPress session state
 */
export function useWalletConnect() {
  const [wallet, setWallet] = useState({
    connected: false,
    address: null,
    chainId: null,
  });
  const [user, setUser] = useState({
    id: window.t24Governance?.currentUserId || 0,
    name: window.t24Governance?.currentUserName || '',
    votingPower: 0,
    hasRole: false,
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Check if wallet is already connected on mount
  useEffect(() => {
    checkExistingConnection();
  }, []);

  /**
   * Check for existing WordPress session + wallet
   */
  const checkExistingConnection = useCallback(async () => {
    // Check if user is already logged in via WordPress
    if (window.t24Governance?.isLoggedIn) {
      setUser({
        id: window.t24Governance.currentUserId,
        name: window.t24Governance.currentUserName,
        votingPower: 0, // Will be fetched on demand
        hasRole: false,
      });

      // Try to get wallet address from MetaMask if available
      if (typeof window.ethereum !== 'undefined') {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts && accounts.length > 0) {
            setWallet({
              connected: true,
              address: accounts[0],
              chainId: await window.ethereum.request({ method: 'eth_chainId' }),
            });
          }
        } catch (err) {
          console.error('Failed to get connected accounts:', err);
        }
      }
    }
  }, []);

  /**
   * Connect wallet and login/register user
   */
  const connectWallet = useCallback(async (walletType = 'metamask') => {
    setIsConnecting(true);
    setError(null);

    try {
      let provider;
      let walletAddress;
      let chainId;

      // Handle different wallet types
      if (walletType === 'walletconnect') {
        // WalletConnect support (for mobile wallets via QR code)
        throw new Error('WalletConnect will be supported in a future update. Please use MetaMask or another browser wallet for now.');
      } else {
        // For metamask, coinbase, trust, and injected wallets
        // They all use window.ethereum

        // Check if injected provider exists
        if (typeof window.ethereum === 'undefined') {
          throw new Error('Please install MetaMask or another Web3 wallet');
        }

        provider = window.ethereum;

        // Request account access
        const accounts = await provider.request({
          method: 'eth_requestAccounts',
        });

        if (!accounts || accounts.length === 0) {
          throw new Error('No accounts found');
        }

        walletAddress = accounts[0];
        chainId = await provider.request({ method: 'eth_chainId' });
      }

      // Step 1: Get nonce from backend
      const nonceResponse = await fetch(
        `${window.t24Governance.ajaxUrl}?action=t24_web3_get_nonce&wallet_address=${walletAddress}`
      );
      const nonceData = await nonceResponse.json();

      if (!nonceData.success) {
        throw new Error(nonceData.data?.message || 'Failed to get nonce');
      }

      // Step 2: Request signature from wallet
      const message = nonceData.data.message;
      const signature = await provider.request({
        method: 'personal_sign',
        params: [message, walletAddress],
      });

      // Step 3: Send signature to backend for login/registration
      const formData = new FormData();
      formData.append('action', 't24_web3_login');
      formData.append('wallet_address', walletAddress);
      formData.append('signature', signature);
      formData.append('message', message);

      const loginResponse = await fetch(window.t24Governance.ajaxUrl, {
        method: 'POST',
        body: formData,
      });

      const loginData = await loginResponse.json();

      if (!loginData.success) {
        throw new Error(loginData.data?.message || 'Login failed');
      }

      // Update state
      setWallet({
        connected: true,
        address: walletAddress,
        chainId: chainId,
      });

      setUser({
        id: loginData.data.user_id,
        name: loginData.data.user_name,
        votingPower: loginData.data.voting_power || 0,
        hasRole: loginData.data.has_governance_role || false,
      });

      // Update global state
      window.t24Governance.isLoggedIn = true;
      window.t24Governance.currentUserId = loginData.data.user_id;
      window.t24Governance.currentUserName = loginData.data.user_name;

      setIsConnecting(false);
      return { success: true, user: loginData.data };

    } catch (err) {
      console.error('Wallet connection error:', err);
      setError(err.message);
      setIsConnecting(false);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Disconnect wallet and logout
   */
  const disconnectWallet = useCallback(async () => {
    // Logout from WordPress
    window.location.href = window.t24Governance.siteUrl + '/wp-login.php?action=logout';
  }, []);

  /**
   * Switch to Polygon network
   */
  const switchToPolygon = useCallback(async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }], // Polygon Mainnet = 137 = 0x89
      });

      const newChainId = await window.ethereum.request({ method: 'eth_chainId' });
      setWallet((prev) => ({ ...prev, chainId: newChainId }));

      return true;
    } catch (switchError) {
      // Chain not added, try adding it
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: '0x89',
              chainName: 'Polygon Mainnet',
              nativeCurrency: {
                name: 'MATIC',
                symbol: 'MATIC',
                decimals: 18,
              },
              rpcUrls: ['https://polygon-rpc.com'],
              blockExplorerUrls: ['https://polygonscan.com'],
            }],
          });
          return true;
        } catch (addError) {
          console.error('Failed to add Polygon network:', addError);
          return false;
        }
      }
      console.error('Failed to switch to Polygon:', switchError);
      return false;
    }
  }, []);

  // Listen for account changes
  useEffect(() => {
    if (typeof window.ethereum !== 'undefined') {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          // User disconnected wallet
          setWallet({ connected: false, address: null, chainId: null });
        } else if (accounts[0] !== wallet.address) {
          // User switched account - reconnect
          connectWallet();
        }
      };

      const handleChainChanged = (chainId) => {
        setWallet((prev) => ({ ...prev, chainId }));
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [wallet.address, connectWallet]);

  return {
    wallet,
    user,
    isConnecting,
    error,
    connectWallet,
    disconnectWallet,
    switchToPolygon,
    isLoggedIn: window.t24Governance?.isLoggedIn || false,
    hasWallet: typeof window.ethereum !== 'undefined',
  };
}
